import React from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 1, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Speaking() {
  const CHECKOUT_URL = "https://buy.stripe.com/bJe6oH7VFbqi3PC9L5cfK06";
  const BOOKING_URL = "https://link.nordiccrm.ca/widget/bookings/dorman-ernest-baltazar-personal-calendar-qg2bbc8wa";

  return (
    <div className="w-full bg-kin-black">
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-6 pt-32 pb-20 overflow-hidden text-center">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[80vw] h-[60vh] bg-kin-gold/5 blur-[140px] rounded-full pointer-events-none"></div>
        <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center">
          <FadeUp>
            <p className="text-kin-gold tracking-[0.4em] text-[10px] md:text-xs uppercase font-light mb-8">
              Keynote Speaker • Corporate Leader • Resilience Expert
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <h1 className="text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-serif text-balance leading-[1.1] text-kin-ivory mb-10">
              Turning Adversity Into <span className="italic">Strategy, Leadership,</span> And Lasting Impact.
            </h1>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className="max-w-3xl mx-auto text-lg md:text-xl text-kin-ivory/50 font-light leading-relaxed tracking-wide mb-14 px-4">
              From surviving a drive-by shooting in South Central Los Angeles to leading high-growth ventures and purpose-driven organizations, Dorman Baltazar equips audiences with practical frameworks for resilience, leadership, psychological safety, and sustainable performance.
            </p>
          </FadeUp>
          <FadeUp delay={0.5} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full px-4">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button variant="primary" className="w-full sm:px-12">Book Dorman To Speak</Button>
            </a>
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button variant="secondary" className="w-full sm:px-12">Schedule A Speaking Engagement</Button>
            </a>
          </FadeUp>
        </div>
      </section>

      {/* FEATURED IMAGE SECTION */}
      <section className="py-20 md:py-32 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <FadeUp className="relative group">
            <div className="aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden relative border border-kin-ivory/5">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/9c5adfa6bba615730f4558013336717062b18ab46d11ed47c31c88c932e61ffbf11018f89f2243f6aef891f7c9f9eda4/images/aW1hZ2UvanBlZw==/68d6537db3932682a335733b6ae3e309.jpeg" 
                alt="Dorman Baltazar - Keynote Speaker" 
                className="hidden" 
              />
              <img 
                src="https://media-manager-c.questera.ai/greta-media/9c5adfa6bba615730f4558013336717062b18ab46d11ed47c31c88c932e61ffbf11018f89f2243f6aef891f7c9f9eda4/images/aW1hZ2UvanBlZw==/e3a999aa4bef11412c7c81e9adfb1276.jpeg" 
                alt="Dorman Baltazar - Keynote Speaker" 
                className="object-cover w-full h-full grayscale opacity-80 mix-blend-luminosity transition-all duration-1000 group-hover:scale-105 group-hover:opacity-100 group-hover:grayscale-0" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-kin-black/60 via-transparent to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-b border-r border-kin-gold/20 -z-10 group-hover:border-kin-gold/40 transition-colors"></div>
          </FadeUp>
        </div>
      </section>

      {/* BIO SECTION */}
      <section className="py-32 md:py-56 px-6 bg-[#09090b] border-y border-kin-ivory/5">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 lg:gap-32">
          <div className="lg:w-1/2">
            <FadeUp>
              <h2 className="text-4xl md:text-6xl font-serif text-kin-ivory leading-tight mb-12">
                Meet <span className="italic text-kin-gold">Dorman Baltazar</span>
              </h2>
              <div className="space-y-8 text-lg md:text-xl text-kin-ivory/60 font-light leading-relaxed tracking-wide">
                <p>
                  Dorman Ernest Baltazar is a high-impact keynote speaker, corporate leader, and resilience expert who transforms audiences by turning profound adversity into a blueprint for purposeful execution.
                </p>
                <p>
                  From surviving a drive-by shooting in South Central Los Angeles to leading cutting-edge ventures as a CEO, Dorman delivers raw, hard-won insights on grit, psychological safety, and sustainable peak performance.
                </p>
                <p>
                  As CEO and Partner at Grubdeli Canada Inc. and CEO and Founder of Kingdom Inspira Nova Studio, Dorman bridges frontline survival with executive leadership and strategic innovation.
                </p>
                <p>
                  His work combines business leadership, community empowerment, wellness, and creative storytelling into actionable frameworks audiences can immediately apply.
                </p>
              </div>
            </FadeUp>
          </div>
          <div className="lg:w-1/2 flex items-center justify-center">
            <FadeUp delay={0.2} className="relative">
              <div className="text-[15vw] font-serif text-kin-gold/5 italic select-none pointer-events-none absolute -top-1/2 left-1/2 -translate-x-1/2">
                Impact
              </div>
              <div className="w-px h-64 bg-gradient-to-b from-kin-gold/50 via-kin-gold/10 to-transparent"></div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SIGNATURE APPROACH SECTION */}
      <section className="py-32 md:py-48 px-6 text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory mb-12">
              Where Mental Mastery Meets <br/>
              <span className="italic text-kin-gold">Strategic Execution</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="text-lg md:text-xl text-kin-ivory/60 font-light leading-relaxed tracking-wide mb-16">
              Dorman’s approach to leadership is shaped by an uncommon combination of:
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-6 mb-16 px-4">
              {['entrepreneurial leadership', 'athletic discipline', 'resilience training', 'breath awareness', 'meditation', 'high-performance execution'].map((item, i) => (
                <div key={i} className="text-xs md:text-sm uppercase tracking-[0.2em] text-kin-gold/70 border border-kin-gold/10 px-6 py-3 rounded-full">
                  {item}
                </div>
              ))}
            </div>
            <p className="text-lg md:text-xl text-kin-ivory/60 font-light leading-relaxed tracking-wide max-w-3xl mx-auto">
              As a former cross-country mountain bike racer, he understands the discipline, pacing, and endurance required to succeed in demanding environments. His keynotes provide practical tools for managing pressure, improving focus, and building sustainable performance cultures.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* SPEAKING TOPICS SECTION */}
      <section className="py-32 md:py-48 px-6 bg-[#09090b] border-t border-kin-ivory/5">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory mb-24 text-center">
              Signature <span className="italic text-kin-gold">Speaking Topics</span>
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                title: "Resilience By Design",
                desc: "Transforming adversity, trauma, and systemic obstacles into purpose-driven leadership and operational excellence."
              },
              {
                title: "The Inclusive Screen",
                desc: "Leveraging cinematic storytelling and media to empower Black and Indigenous communities while driving meaningful representation."
              },
              {
                title: "The Athlete’s Mindset In Business",
                desc: "Applying endurance, active focus, mental clarity, and breath awareness to modern leadership and organizational success."
              }
            ].map((topic, i) => (
              <FadeUp key={i} delay={i * 0.15} className="flex flex-col group">
                <div className="h-[1px] w-full bg-kin-ivory/10 mb-10 group-hover:bg-kin-gold transition-all duration-500"></div>
                <h3 className="text-2xl font-serif text-kin-ivory mb-6 leading-tight group-hover:text-kin-gold transition-colors">
                  {topic.title}
                </h3>
                <p className="text-sm md:text-base text-kin-ivory/40 font-light leading-relaxed">
                  {topic.desc}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* DISCOVERY CALL SECTION (NEW) */}
      <section className="py-32 md:py-48 px-6 border-t border-kin-ivory/5 bg-kin-black">
        <div className="max-w-4xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-3xl md:text-5xl font-serif text-kin-ivory mb-8">
              Prefer To <span className="italic text-kin-gold">Talk First?</span>
            </h2>
            <p className="max-w-2xl mx-auto text-lg md:text-xl text-kin-ivory/50 font-light leading-relaxed tracking-wide mb-12">
              Schedule a discovery call with Dorman to discuss your event, audience, goals, and speaking engagement requirements.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" className="px-12 py-5">Schedule A Discovery Call</Button>
            </a>
          </FadeUp>
        </div>
      </section>

      {/* AUDIENCE SECTION */}
      <section className="py-32 md:py-48 px-6">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-20">
          <div className="lg:w-1/3">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl font-serif text-kin-ivory leading-tight">
                Ideal <span className="italic text-kin-gold">For</span>
              </h2>
            </FadeUp>
          </div>
          <div className="lg:w-2/3">
            <FadeUp delay={0.2} className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
              {[
                "Corporate Teams", "Executive Leadership Retreats", "Conferences",
                "Non-Profit Organizations", "Community Initiatives", "Universities",
                "Colleges", "Youth Leadership Programs", "Entrepreneur Communities",
                "Creator Ecosystems"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 text-kin-ivory/60 text-lg font-light border-b border-kin-ivory/5 pb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-kin-gold/40"></span>
                  {item}
                </div>
              ))}
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL SECTION */}
      <section className="py-32 md:py-48 px-6 bg-[#09090b] text-center border-y border-kin-ivory/5">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <div className="text-kin-gold/20 text-8xl font-serif leading-none mb-4 italic">“</div>
            <p className="text-3xl md:text-6xl font-serif text-kin-ivory italic leading-snug mb-12">
              Vision becomes structure.
            </p>
            <div className="flex flex-col items-center">
              <div className="w-12 h-px bg-kin-gold mb-8"></div>
              <p className="text-kin-ivory font-serif text-xl tracking-wide">Dorman Baltazar</p>
              <p className="text-kin-gold text-[10px] uppercase tracking-[0.3em] mt-2">CEO / Founder / Producer • Kingdom Inspira Nova Studio</p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-40 md:py-60 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(198,160,98,0.03)_0%,_transparent_70%)]"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <h2 className="text-3xl md:text-6xl font-serif text-kin-ivory leading-tight mb-8">
              Bring A Transformational Voice <br/> To Your Next Event.
            </h2>
            <p className="max-w-2xl mx-auto text-lg md:text-xl text-kin-ivory/50 font-light leading-relaxed tracking-wide mb-16">
              Equip your audience with practical resilience, strategic clarity, and leadership frameworks designed for real-world impact.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full px-4">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button variant="primary" className="w-full sm:px-16 py-5">Book Dorman To Speak</Button>
            </a>
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button variant="secondary" className="w-full sm:px-16 py-5">Secure Your Engagement</Button>
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}